/**
 * PlotChip — the name and value of the line under the pointer, beside the
 * pointer (charts.md, "The line under the pointer").
 *
 * The engine decides *which* line (`onHoverPlot`) and thickens it; this only
 * says what it is. The words come from the same `legendRows` call the value
 * legend makes, so the chip and the legend cannot disagree.
 *
 * **Its own state, not the chart's.** The hit moves with every pointer move,
 * and holding it in `ChartGroupView` would re-render the whole chart host sixty
 * times a second. The host hands in a `bind` slot and this component puts its
 * setter there, so only the chip re-renders.
 */

import { useEffect, useMemo, useRef, useState, type CSSProperties, type MutableRefObject } from "react";
import { createPortal } from "react-dom";
import { chipName, chipPlacement, legendRows, type PanePlan, type PlotHit } from "@qufolio/chart-engine";
import { themeCssVariables } from "@qufolio/model";
import { useTheme } from "./theme";
import type { LegendControls } from "./ValueLegend";

/** The hit and the bar it was read at, or `null` for no chip. */
export type PlotChipState = { hit: PlotHit; index: number } | null;

export interface PlotChipProps {
  /** Where the host calls in with a new hit. */
  bind: MutableRefObject<((state: PlotChipState) => void) | null>;
  planFor: (paneId: string) => PanePlan | undefined;
  values: Readonly<Record<string, readonly number[]>>;
  symbol?: string;
  controls?: Pick<LegendControls, "nameOf" | "familyOf">;
}

/** Until the chip has been measured once, roughly one name and one number. */
const FIRST_WIDTH = 140;
const FIRST_HEIGHT = 22;

export function PlotChip({ bind, planFor, values, symbol, controls }: PlotChipProps) {
  const [state, setState] = useState<PlotChipState>(null);
  // Portalled, so it carries the theme's variables itself (see SparkCard).
  const theme = useTheme();
  const themeVars = useMemo(() => themeCssVariables(theme) as CSSProperties, [theme]);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bind.current = setState;
    return () => {
      bind.current = null;
    };
  }, [bind]);

  if (!state) return null;
  const { hit, index } = state;
  const plan = planFor(hit.paneId);
  if (!plan) return null;
  const rows = legendRows(plan.series, values, index, symbol, controls?.nameOf);
  const row = rows.find((r) => r.plotId === hit.plotId);
  // A marker with nothing to print at this bar (a signal that did not fire,
  // a dot whose value is blank) still gets its name: the dot is on screen.
  const value = row?.cells[0]?.value;
  if (!row) return null;

  const family = controls?.familyOf(hit.plotId);
  const familyRows = family
    ? rows.filter((r) => controls?.familyOf(r.plotId)?.key === family.key).length
    : 0;

  // **Fixed, on `body`, in viewport pixels.** It was absolute inside the chart,
  // in canvas pixels, and in a short lower pane it showed nowhere the owner
  // could see (2026-10-07); outside the chart, no box of the chart's can offset
  // or clip it.
  //
  // **Beside the crosshair, not the pointer** (owner, 2026-10-07): left of the
  // vertical line and sitting on the horizontal one, flipped right at the plot
  // area's left edge (`chipPlacement`). The size is the previous render's,
  // which is close enough: it changes only with the text.
  const at = chipPlacement(hit, {
    width: ref.current?.offsetWidth ?? FIRST_WIDTH,
    height: ref.current?.offsetHeight ?? FIRST_HEIGHT,
  });

  return createPortal(
    <div
      ref={ref}
      role="status"
      aria-live="off"
      style={{
        ...themeVars,
        position: "fixed",
        left: at.left,
        top: Math.max(0, at.top),
        pointerEvents: "none",
        zIndex: 10000,
        display: "flex",
        gap: 8,
        alignItems: "baseline",
        padding: "2px 7px",
        borderRadius: 4,
        border: `1px solid ${row.color ?? "var(--qf-text-muted)"}`,
        background: "var(--qf-surface)",
        color: "var(--qf-text)",
        fontSize: 12,
        fontVariantNumeric: "tabular-nums",
        whiteSpace: "nowrap",
      }}
    >
      <span style={{ color: "var(--qf-text-secondary)" }}>{chipName(row.name, family?.label, familyRows)}</span>
      {value ? <span>{value}</span> : null}
    </div>,
    document.body,
  );
}
